import { DirtyTextBoxes } from '@/components/PdfEditor/types';
import { PageMeta } from '@/components/PdfEditor/rendering/DocumentMeta';

export type DocumentType = string | ArrayBuffer | null | undefined;

/* ------------------------- PDF Editor ------------------------- */
export enum EditorLayers {
  IMAGE_LAYER = 'IMAGE_LAYER',
  TEXT_LAYER = 'TEXT_LAYER',
  ANNOTATION_LAYER = 'ANNOTATION_LAYER',
  SHAPES_LAYER = 'SHAPES_LAYER',
  FORM_LAYER = 'FORM_LAYER',
}

// === Extracted Text Items ===
export type PDFJSTextItem = {
  dir: string;
  fontName: string;
  hasEOL: boolean;
  height: number;
  width: number;
  str: string;
  transform: number[];
  fillColor: [number, number, number];
  strokeColor: [number, number, number];
  fontSize: number;
  renderingMode: number;
};

// === UI Editable boxes ===
export type Position = {
  x: number;
  y: number;
};

export type Coordinates = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TextCoordinates = Coordinates & { fontSize: number };

export type EditableTextItem = {
  id: string;
  originalPdfCoordinates: TextCoordinates;
  currentPdfCoordinates: TextCoordinates;
  originalCanvasCoordinates: TextCoordinates;
  currentCanvasCoordinates: TextCoordinates;
  scaleX: number; // TODO: remove and centralize in the canvas
  scaleY: number; // TODO: remove and centralize in the canvas
  text: string;
  originalText?: string;
  isBold?: boolean;
  isItalic?: boolean;
  isEdited: boolean;
  isDeleted: boolean;
  isDuplicated: boolean;
  isNew: boolean;
  isEmbedded: boolean;
  isDirty: boolean;
  /**
   * Set once the user changes the block's typography (font family, bold, italic,
   * or size). Turns off the original-width adjustment: it reproduces the
   * original glyph run and stops being meaningful once the font differs. Not set
   * by text edits, moves, or color changes. See keepsWidthCompensation.
   */
  isTypographyChanged?: boolean;
  originalFontName: string;
  fontName: string;
  /**
   * PostScript name of the item's embedded font (pdf.js fontObj.name). Sent as
   * the save-operation font for embedded items — the backend resolves it
   * against the document's own fonts. Set at parse time and when a document
   * font is committed from the picker; undefined for catalog-font items.
   */
  psFontName?: string;
  angle: number;
  renderingMode: number;
  autoFocus?: boolean;
  fillColor?: string;
  strokeColor?: number[];
  bgMode?: 'WHITE' | 'BACKGROUND'; // For dirty text items, stores the mode with which its original position should be patched
};

export type PageMetadata = {
  id: string;
  originalPageNumber: number | -1;
  thumbnailPageNumber?: number;
  page: PageMeta;
  renderedPage: { width: number; height: number };
  pageIndex: number;
};

export type PageTextItems = Record<number, EditableTextItem[]>;

export type PageItems = {
  [pageNumber: number]: PageMetadata;
};

export type DragInfo = {
  isMouseDown: boolean;
  offset: { x: number; y: number };
};

export type EditingContext = {
  pagesMetadata: PageItems;
  pageTextItems: PageTextItems;
  pageImageItems: PageImageItems;
  pageAnnotations: PageAnnotations;
  formFields: FormFields;
  addedPages: number[];
  deletedPages: number[];
  zIndexMap: Record<string, number>;
  documentPassword: string | null;
  dirtyTextItems: DirtyTextBoxes;
};

/* ------------------------- Images ------------------------- */

export type ImageItem = {
  id: string;
  fileId: string;
  url: string;
  rotation?: number;
  currentPdfCoordinates: Coordinates;
  currentCanvasCoordinates: Coordinates;
};

export type PageImageItems = Record<string, ImageItem[]>;

export type DbImageItem = {
  id: string;
  url: string;
  // Persisted to IndexedDB as raw bytes + MIME type. Storing a Blob/File directly
  // throws `DataCloneError: BlobURLs are not yet supported` in some WebKit contexts
  // (iOS private browsing / in-app WKWebViews), so we serialize an ArrayBuffer instead.
  buffer?: ArrayBuffer;
  type?: string;
  // TODO(deprecate): remove once legacy Blob-based records have aged out of users'
  // IndexedDB. Records persisted before the ArrayBuffer migration stored a Blob directly.
  blob?: Blob;
  name?: string;
  tag: 'image' | 'signature' | 'annotation';
};

export type DbImageItems = {
  [id: string]: DbImageItem;
};

/* ------------------------- Annotations ------------------------- */

export type Annotation = {
  id: string;
  url: string;
  currentPdfCoordinates: Coordinates;
  currentCanvasCoordinates: Coordinates;
  rotation?: number;
  paths: Paths;
  color: string;
  lineWidth?: number;
  type: DrawingTypes;
  scale: number;
};

export type PageAnnotations = {
  [pageNumber: string]: Annotation[];
};

export enum DrawingTypes {
  Highlight = 'HIGHLIGHT',
  Draw = 'DRAW',
}

export type DrawingOptions = {
  type: DrawingTypes;
  colorHex: string;
  lineWidth?: number;
};

export type Point = { x: number; y: number; lineWidth?: number };

export type Paths = Point[][];

/* ------------------------- Forms ------------------------- */

export enum FormTypes {
  TEXT = 'TEXT',
  TEXTAREA = 'TEXTAREA',
  DROPDOWN_LIST = 'DROPDOWN_LIST',
  RADIO_BUTTON = 'RADIO_BUTTON',
  CHECKBOX = 'CHECKBOX',
}

export type TextFormField = {
  id: string;
  name: string;
  type: FormTypes;
  value: string;
  color: string;
  borderColor: string;
  textAlign: 'left' | 'center' | 'right';
  canvasCoordinates: TextCoordinates;
  pdfCoordinates: TextCoordinates;
  existing: boolean;
};

export type FormField = TextFormField;

export type FormFields = Record<number, FormField[]>;
